/** @jsxImportSource @emotion/react */
import { css } from "@emotion/react";

import { faTrash } from "@fortawesome/pro-regular-svg-icons";
import { useEffect, useRef, useState } from "react";
import { useSelector } from "react-redux";
import { toast } from "react-toastify";
import { Input, InputGroup } from "reactstrap";
import { FormFileSelectFieldReadFormat } from "../../actions/Tenants/config/constantsTyped";
import { MimeType } from "../../constants/misc";
import useAvosApi from "../../utils/useAvosApiHook";
import { Button } from "../Helper/Button";
import { useBlobUrl } from "./ImageRadiButtons";


export interface FileSelectProps {
    onSelect: (fileName: string, fileContent: string) => void;
    onSelectImage: (url: string | null) => void;
    accept: MimeType[];
    readFormat?: FormFileSelectFieldReadFormat;
    upload_config_image?: boolean;
    fileObject?: any;
}


export function FileSelect({ onSelect, onSelectImage, accept = [], readFormat = FormFileSelectFieldReadFormat.Base64, upload_config_image, fileObject }: FileSelectProps) {
    const fileInputRef = useRef<HTMLInputElement>(null);
    const isValidFileType = (fileType: MimeType, accept: MimeType[]) => {
        return accept.includes(fileType);
    };

    const tenant_id = useSelector((state: any) => state.tenants.current.tenant_id);
    const [uploadedImage, setUploadedImage] = useState<string | null>(null);

    const [UploadImage, uploadedBlob, uploadError] = useAvosApi(
        `/tenants/${tenant_id}/configs/upload-image`,
        "POST"
    );

    const blobName = fileObject?.blob_name ?? "";
    const shouldFetchBlobUrl = !!blobName && !fileObject?.image_url && !uploadedImage;
    const { data: resolvedBlobUrl, isLoading: isLoadingBlobUrl } = useBlobUrl(blobName, { enabled: shouldFetchBlobUrl });

    useEffect(() => {
        if (uploadedBlob?.image_url && uploadedBlob?.blob_name) {
            onSelectImage(uploadedBlob.blob_name);
            setUploadedImage(uploadedBlob.image_url);
        }
    }, [uploadedBlob?.image_url, uploadedBlob?.blob_name]);


    useEffect(() => {
        if (!blobName || fileObject?.image_url || uploadedImage || isLoadingBlobUrl) {
            return;
        }

        if (resolvedBlobUrl) {
            setUploadedImage(resolvedBlobUrl);
        }
    }, [blobName, fileObject?.image_url, uploadedImage, resolvedBlobUrl, isLoadingBlobUrl]);


    const uploadImage = async (image_name: string, base64: string) => {
        try {
            if (typeof UploadImage === "function") {
                await UploadImage(
                    {
                        image_name,
                        base64,
                        type: "image",
                    },
                    {
                        endpoint: `/tenants/${tenant_id}/configs/upload-image`,
                        method: "POST",
                    }
                );

            } else {
                throw new Error("API is not available.");
            }
        } catch (error) {
            const errorMessage = uploadError instanceof Error ? uploadError.message : (error as Error).message;
            toast.error(`Failed to upload image: ${errorMessage}`, {
                autoClose: 3000,
            });
        }
    };
    const fileChangeHandler = (e) => {
        const file = e.target.files[0];
        if (file) {
            if (isValidFileType(file.type as MimeType, accept)) {
                const reader = new FileReader();
                reader.onload = async (event) => {
                    try {
                        const content = event?.target?.result as string;

                        if (upload_config_image) {
                            uploadImage(file.name, content);
                        } else {
                            await onSelect(file.name, content);
                        }

                    } catch (error: any) {
                        toast.error(error.message, {
                            autoClose: 3000,
                        });
                    }
                };
                if (readFormat === FormFileSelectFieldReadFormat.Base64) {
                    reader.readAsDataURL(file); // Use readAsDataURL to get base64-encoded string
                } else if (readFormat === FormFileSelectFieldReadFormat.Raw) {
                    reader.readAsArrayBuffer(file); // Use readAsArrayBuffer to get raw binary data
                }
            } else {
                toast.error(`Invalid file type. Please select a file of type ${accept.join(",")}`, {
                    autoClose: 3000,
                });
            }
        }
    };

    useEffect(() => {
        if (fileObject?.image_url === null && fileInputRef.current) {
            fileInputRef.current.value = "";
            setUploadedImage(null);
        }
    }, [fileObject?.image_url]);

    // Extract filename from blob_name (e.g., "config/tenant123/abc123_logo.webp" → "logo.webp")
    const getDisplayName = () => {
        if (blobName) {
            const parts = blobName.split("/");
            const filename = parts[parts.length - 1];
            // Remove hash prefix if present (e.g., "abc123_logo.webp" → "logo.webp")
            return filename.includes("_") ? filename.split("_").slice(1).join("_") : filename;
        }
        return null;
    };

    const handleRemove = () => {
        if (fileInputRef.current) {
            fileInputRef.current.value = "";
        }
        setUploadedImage(null);
        onSelectImage(null);
    };

    const displayName = getDisplayName();
    const hasExistingFile = !!(uploadedImage || fileObject?.image_url || blobName);

    return (
        <InputGroup>
            {!hasExistingFile ? (
                // Show file input when no image is selected
                <Input
                    type="file"
                    accept={accept.join(",")}
                    onChange={fileChangeHandler}
                    className="file-input"
                    innerRef={fileInputRef}
                />
            ) : (
                // Show image preview with filename and remove button when image is selected
                <div
                    className="d-flex align-items-center gap-3 p-2 border rounded w-100"
                    css={css`background-color: #f8f9fa;`}
                >
                    <img
                        src={uploadedImage || fileObject?.image_url}
                        alt="Selected image"
                        css={css`
                            object-fit: contain; 
                            border: 1px solid #dee2e6; 
                            border-radius: 4px;
                            background: white;
                        `}
                        width="60px"
                        height="60px"
                    />
                    <div className="flex-grow-1">
                        <div className="fw-medium" title={blobName}>
                            {displayName || "Uploaded image"}
                        </div>
                        <div className="text-muted small">Click remove to change</div>
                    </div>
                    <Button
                        size="sm"
                        color="danger"
                        outline
                        onClick={handleRemove}
                        icon={faTrash}
                    />
                </div>
            )}
        </InputGroup>
    );
}
